import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const propertyTypes = [
  {
    id: 'commercial',
    title: 'Commercial',
    image: 'https://readdy.ai/api/search-image?query=High-end%20luxury%20commercial%20skyscraper%20with%20aerodynamic%20streamlined%20glass%20curtain%20wall%20facade%2C%20futuristic%20flowing%20curved%20architecture%2C%20sweeping%20elegant%20lines%2C%20premium%20financial%20district%20tower%2C%20warm%20golden%20hour%20lighting%2C%20sophisticated%20contemporary%20design%2C%20clean%20architectural%20photography%2C%20blue%20sky%20with%20soft%20clouds%2C%20minimal%20background%2C%20ultra-modern%20sleek%20aesthetic&width=600&height=400&seq=commercial-property-02&orientation=landscape&nocache=true',
    summary: 'Premium office buildings and corporate spaces in prime business districts, delivering stable rental income and strong capital appreciation.',
    detail: 'Our commercial property portfolio focuses on Grade A office towers and corporate headquarters in Asia Pacific\'s most dynamic financial centers. We target assets with blue-chip tenants, long-term lease structures, and value-add potential through strategic repositioning. Each investment undergoes rigorous due diligence to ensure sustainable cash flow and capital growth.',
  },
  {
    id: 'residence',
    title: 'Residence',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20penthouse%20living%20room%20interior%20view%20looking%20out%20through%20floor-to-ceiling%20glass%20windows%2C%20breathtaking%20panoramic%20vista%20of%20vast%20green%20central%20park%20with%20distant%20city%20skyline%20silhouette%2C%20warm%20golden%20sunset%20light%20flooding%20into%20elegant%20modern%20living%20space%2C%20marble%20floors%20and%20rich%20wood%20accents%2C%20sophisticated%20minimalist%20designer%20furniture%20in%20foreground%2C%20elevated%20commanding%20perspective%20high%20above%20the%20city%2C%20feeling%20of%20prestige%20and%20exclusivity%2C%20architectural%20interior%20photography%2C%20warm%20luxurious%20residential%20atmosphere&width=600&height=400&seq=residence-property-04&orientation=landscape&nocache=true',
    summary: 'High-quality residential developments designed for modern living, combining lifestyle amenities with enduring asset value across Asia Pacific.',
    detail: 'Our residential investments span premium apartments, condominiums, and gated communities in high-growth urban corridors. We prioritise locations with strong demographic tailwinds, excellent connectivity, and lifestyle infrastructure. Each project is selected for its ability to deliver consistent occupancy rates and long-term appreciation driven by urbanisation trends.',
  },
  {
    id: 'mixed-use',
    title: 'Mixed-Use',
    image: 'https://readdy.ai/api/search-image?query=Grand%20luxury%20mixed-use%20skyscraper%20complex%20with%20integrated%20retail%20podium%20featuring%20upscale%20shops%20and%20restaurants%2C%20premium%20office%20floors%20in%20the%20middle%2C%20and%20high-end%20residential%20towers%20rising%20above%2C%20striking%20modern%20architecture%20with%20dramatic%20glass%20and%20steel%20facade%2C%20vibrant%20urban%20plaza%20at%20street%20level%2C%20warm%20golden%20hour%20lighting%2C%20bustling%20cosmopolitan%20atmosphere%2C%20architectural%20photography%2C%20impressive%20scale%2C%20clean%20sophisticated%20composition&width=600&height=400&seq=mixed-use-property-02&orientation=landscape&nocache=true',
    summary: 'Integrated developments blending retail, office and residential spaces to create dynamic urban ecosystems with diversified revenue streams.',
    detail: 'Our mixed-use strategy targets transformative urban developments that combine commercial, residential, and lifestyle components into cohesive communities. These assets benefit from cross-sector demand resilience, creating natural hedges against market cycles. We focus on transit-oriented developments with curated tenant mixes that enhance footfall and long-term value.',
  },
];

export default function Home() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const expandedCard = expandedId ? propertyTypes.find((p) => p.id === expandedId) : null;

  return (
    <div className="min-h-screen pt-20 md:pt-24">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[420px] md:h-[580px] w-full overflow-hidden">
        <img
          src="https://static.readdy.ai/image/de83ab16ca59df517cba587afca539d2/9173953e76f7ccf19ed886a5ea84bea9.png"
          alt="Modern architecture"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-brand-gold text-2xl md:text-4xl font-heading font-medium text-center px-6 max-w-3xl leading-snug">
            Employing a consistent investment approach
          </h1>
        </div>
      </section>

      {/* Professional Investors Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left images */}
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Group%20of%20professional%20business%20investors%20standing%20by%20floor-to-ceiling%20windows%20in%20a%20high-rise%20modern%20office%2C%20looking%20out%20at%20panoramic%20golden%20hour%20city%20skyline%2C%20sleek%20glass%20building%20interior%2C%20warm%20sunset%20light%20flooding%20through%20windows%2C%20confident%20sophisticated%20professionals%20in%20tailored%20suits%2C%20corporate%20boardroom%20atmosphere%2C%20back%20view%20looking%20outward%2C%20warm%20amber%20and%20golden%20tones%2C%20editorial%20corporate%20photography%2C%20luxurious%20premium%20feel%2C%20photorealistic&width=800&height=600&seq=about-buildings-01&orientation=landscape&nocache=true"
                alt="Professional investors in high-rise office"
                className="w-full h-64 md:h-80 object-cover object-top rounded-lg"
              />
            </div>

            {/* Right text */}
            <div className="space-y-6">
              <div className="w-8 h-0.5 bg-brand-gold" />
              <h2 className="text-xl md:text-2xl font-heading font-bold text-brand-gold leading-snug">
                A group of professional<br />investors in the property sector
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Deeply ingrained in local knowledge, cultures and region whilst constantly adapting to market conditions.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Property Growth aims to explore for opportunities in unexpected areas.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-gray-700 hover:text-brand-gold transition-colors pt-2"
              >
                LEARN MORE
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Numbered Cards Section */}
      <section className="py-8 md:py-12 bg-brand-light">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600 leading-relaxed">
                A group of professional investors in the property sector
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600 leading-relaxed">
                We identify opportunities that are undervalued
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
              <p className="text-sm text-gray-600 leading-relaxed">
                Focusing only in Asia Pacific commercial real estate investments
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Property Types We Invest In Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="section-container">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-brand-gold mb-2">Property Types We Invest In</h3>
          <p className="text-sm text-gray-600 mb-8 leading-relaxed">
            We strategically invest across diverse property sectors to maximise portfolio resilience and long-term value creation.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {propertyTypes.map((property) => (
              <div
                key={property.id}
                className="group cursor-pointer"
                onClick={() => setExpandedId(property.id)}
              >
                <div className="overflow-hidden rounded-lg">
                  <img
                    src={property.image}
                    alt={property.title}
                    className="w-full h-48 md:h-56 object-cover object-top rounded-lg group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h4 className="mt-4 text-lg md:text-xl font-heading font-bold text-gray-800 group-hover:text-brand-gold transition-colors">
                  {property.title}
                </h4>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  {property.summary}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      {expandedCard && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setExpandedId(null)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={expandedCard.image}
                alt={expandedCard.title}
                className="w-full h-48 md:h-64 object-cover object-top rounded-t-lg"
              />
              <button
                onClick={() => setExpandedId(null)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 md:p-8">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-800 mb-3">
                {expandedCard.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {expandedCard.summary}
              </p>
              <div className="w-8 h-0.5 bg-brand-gold mb-4" />
              <p className="text-sm text-gray-700 leading-relaxed">
                {expandedCard.detail}
              </p>
              <button
                onClick={() => setExpandedId(null)}
                className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-gray-700 hover:text-brand-gold transition-colors"
              >
                CLOSE
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}